import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DataTable from 'react-data-table-component';
import { X, Search, Gavel, Building2, Users, Wallet, Inbox } from 'lucide-react';
import i18n from '../../../../../i18n';
import { OMR } from '../../../../common/icons';
import StatusBadge from '../../../../common/StatusBadge';
import { TableSkeleton } from '../../../../common/skelton/TableSkelton';
import { tableStyles } from '../../../../../styles/components/js/tableStyle';
import { DELIVERY_STATUS } from '../../../../../constants/status';
import { getDeliveryStatusDetails } from '../services/auctionApprovalServices';
import { STATUS_META } from './DeliveryStatusCard';
import '../../../../../styles/components/delivery-status-details-modal.scss';

const t = (key, fallback, opts = {}) => i18n.t(key, { defaultValue: fallback, ...opts });

const fmt = (n, decimals = 2) =>
  new Intl.NumberFormat('en', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number(n) || 0);

const fmtCount = (n) => new Intl.NumberFormat('en').format(Number(n) || 0);

const Money = ({ value, tone }) => (
  <span className={`dsm-money ${tone ? `is-${tone}` : ''}`}>
    <OMR size={10} />
    {fmt(value)}
  </span>
);

// "Al Noor Trading" → "AN"
const initialsOf = (name) =>
  String(name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';

/**
 * Image with an initials fallback (missing URL or broken image).
 * shape: 'square' (auction/group/logo) | 'circle' (people)
 */
const Thumb = ({ src, name, shape = 'square', size = 36 }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [src]);

  const style = { width: size, height: size };
  const cls = `dsm-thumb is-${shape}`;

  if (!src || failed) {
    return (
      <span className={`${cls} is-fallback`} style={style} aria-hidden="true">
        {initialsOf(name)}
      </span>
    );
  }

  return (
    <img
      className={cls}
      style={style}
      src={src}
      alt="" // decorative – the name is shown next to it
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
};

// image + two-line text, used by every "who / what" cell
const MediaCell = ({ src, name, shape, size, title, sub }) => (
  <div className="dsm-cell-media">
    <Thumb src={src} name={name} shape={shape} size={size} />
    <div className="dsm-cell-stack">
      <strong>{title}</strong>
      {sub ? <span>{sub}</span> : null}
    </div>
  </div>
);

const TABS = [
  { key: 'auctions', icon: Gavel, label: () => t('auctions', 'Auctions') },
  { key: 'clients', icon: Building2, label: () => t('ds_clients', 'Clients'), internalOnly: true },
  { key: 'bidders', icon: Users, label: () => t('ds_bidders', 'Bidders') },
  { key: 'payments', icon: Wallet, label: () => t('dsm_bidder_payments', 'Bidder payments') },
];
const awaitingLabel = () => t('dsm_awaiting_finance', 'Awaiting finance');

const PAYMENT_FILTERS = [
  { key: 'all', label: () => t('all', 'All') },
  { key: 'pending', label: () => t('dsm_pending', 'Pending') },
  { key: 'awaiting', label: awaitingLabel },
  { key: 'paid', label: () => t('dsm_completed', 'Completed') },
];

const PAYMENT_STATE_LABEL = {
  paid: () => t('dsm_completed', 'Completed'),
  awaiting: awaitingLabel,
  partial: () => t('dsm_partial', 'Partly paid'),
  pending: () => t('dsm_pending', 'Pending'),
  none: () => '—',
};

/**
 * Detailed listing behind a DeliveryStatusCard.
 *
 * props
 *  open, onClose
 *  type        – card status ('all', 'approved', …)
 *  title       – card title
 *  initialTab  – auctions | clients | bidders | payments
 *  filters     – applied list filters { client, sector, startDate, endDate }
 *  isClientRole
 */
export const DeliveryStatusDetailsModal = ({
  open,
  onClose,
  type,
  title,
  initialTab = 'auctions',
  filters = {},
  isClientRole = false,
}) => {
  const tabs = useMemo(
    () => TABS.filter((tb) => !(isClientRole && tb.internalOnly)),
    [isClientRole]
  );

  const [tab, setTab] = useState(initialTab);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [payment, setPayment] = useState('all');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const dialogRef = useRef(null);
  const requestId = useRef(0);
  const filtersKey = JSON.stringify(filters || {});
  const { icon: StatusIcon, tone } = STATUS_META[type] || STATUS_META.default;

  // reset every time the modal opens (or opens for another card / tab)
  useEffect(() => {
    if (!open) return;
    const safeTab = tabs.some((tb) => tb.key === initialTab) ? initialTab : 'auctions';
    setTab(safeTab);
    setPage(1);
    setSearchInput('');
    setSearch('');
    setPayment('all');
    setSummary(null);
    setRows([]);
  }, [open, type, initialTab, tabs]);

  // debounce the search box
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  // fetch
  useEffect(() => {
    if (!open || !type) return;

    const id = ++requestId.current;
    setLoading(true);
    setError(null);

    const params = { status: type, tab, page, limit };
    if (search) params.search = search;
    if (tab === 'payments' && payment !== 'all') params.payment = payment;
    Object.entries(JSON.parse(filtersKey)).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') params[k] = v;
    });

    getDeliveryStatusDetails(params)
      .then((res) => {
        if (id !== requestId.current) return; // a newer request is in flight
        if (res?.success) {
          setRows(res.data || []);
          setTotal(res.meta?.total || 0);
          setSummary(res.meta?.summary || null);
        } else {
          setRows([]);
          setTotal(0);
          setError(res?.message || t('something_went_wrong', 'Something went wrong'));
        }
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
  }, [open, type, tab, page, limit, search, payment, filtersKey]);

  // Escape to close, lock page scroll, focus the dialog
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const changeTab = (key) => {
    setTab(key);
    setPage(1);
    setPayment('all');
  };

  const sl = (i) => (page - 1) * limit + i + 1;

  const winnerCell = (r) =>
    r.winner_name ? (
      <MediaCell
        src={r.winner_avatar_url}
        name={r.winner_name}
        shape="circle"
        size={30}
        title={r.winner_name}
        sub={`ID ${r.winner_id}`}
      />
    ) : (
      '—'
    );

  const columns = (() => {
    const slCol = { name: '#', width: '60px', cell: (row, i) => sl(i) };

    const payCols = [
      {
        name: t('dsm_total', 'Total'),
        right: true,
        minWidth: '120px',
        cell: (r) => <Money value={r.total} />,
      },
      {
        name: t('dsm_completed', 'Completed'),
        right: true,
        minWidth: '120px',
        cell: (r) => <Money value={r.paid} tone="paid" />,
      },
      {
        name: awaitingLabel(),
        right: true,
        minWidth: '140px',
        cell: (r) => <Money value={r.awaiting} tone="awaiting" />,
      },
      {
        name: t('dsm_pending', 'Pending'),
        right: true,
        minWidth: '120px',
        cell: (r) => <Money value={r.pending} tone="pending" />,
      },
    ];

    switch (tab) {
      case 'clients':
        return [
          slCol,
          {
            name: t('client', 'Client'),
            minWidth: '220px',
            wrap: true,
            cell: (r) => (
              <MediaCell
                src={r.client_logo_url}
                name={r.client_name}
                shape="square"
                size={36}
                title={r.client_name || '—'}
                sub={`ID ${r.client_id}`}
              />
            ),
          },
          {
            name: t('dsm_groups', 'Groups'),
            center: true,
            width: '90px',
            cell: (r) => fmtCount(r.groups_count),
          },
          {
            name: t('auctions', 'Auctions'),
            center: true,
            width: '90px',
            cell: (r) => fmtCount(r.auctions_count),
          },
          {
            name: t('ds_bidders', 'Bidders'),
            center: true,
            width: '90px',
            cell: (r) => fmtCount(r.bidders_count),
          },
          ...payCols,
        ];

      case 'bidders':
        return [
          slCol,
          {
            name: t('bidder', 'Bidder'),
            minWidth: '240px',
            wrap: true,
            cell: (r) => (
              <MediaCell
                src={r.avatar_url}
                name={r.name}
                shape="circle"
                size={36}
                title={
                  <>
                    {r.name || '—'} <span className="dsm-muted">({r.bidder_id})</span>
                  </>
                }
                sub={[r.username, r.email].filter(Boolean).join(' · ')}
              />
            ),
          },
          {
            name: t('auctions', 'Auctions'),
            center: true,
            width: '90px',
            cell: (r) => fmtCount(r.auctions_count),
          },
          ...(isClientRole
            ? []
            : [
                {
                  name: t('ds_clients', 'Clients'),
                  center: true,
                  width: '90px',
                  cell: (r) => fmtCount(r.clients_count),
                },
              ]),
          ...payCols,
        ];

      case 'payments':
        return [
          slCol,
          {
            name: t('auction', 'Auction'),
            minWidth: '240px',
            wrap: true,
            cell: (r) => (
              <MediaCell
                src={r.cover_url}
                name={r.title}
                shape="square"
                size={40}
                title={r.title || '—'}
                sub={`MZAD ${r.id}${!isClientRole && r.client_name ? ` · ${r.client_name}` : ''}`}
              />
            ),
          },
          {
            name: t('winner', 'Winner'),
            minWidth: '180px',
            wrap: true,
            cell: winnerCell,
          },
          ...payCols,
          {
            name: t('status', 'Status'),
            minWidth: '140px',
            cell: (r) => (
              <span className={`dsm-state is-${r.payment_state}`}>
                {(PAYMENT_STATE_LABEL[r.payment_state] || PAYMENT_STATE_LABEL.none)()}
              </span>
            ),
          },
        ];

      default: // auctions
        return [
          slCol,
          {
            name: t('auction', 'Auction'),
            minWidth: '240px',
            wrap: true,
            cell: (r) => (
              <MediaCell
                src={r.cover_url}
                name={r.title}
                shape="square"
                size={44}
                title={r.title || '—'}
                sub={`MZAD ${r.id}`}
              />
            ),
          },
          {
            name: t('group', 'Group'),
            minWidth: '200px',
            wrap: true,
            cell: (r) => (
              <MediaCell
                src={r.group_image_url}
                name={r.group_name}
                shape="square"
                size={32}
                title={r.group_name || '—'}
                sub={r.project_code || ''}
              />
            ),
          },
          ...(isClientRole
            ? []
            : [
                {
                  name: t('client', 'Client'),
                  minWidth: '180px',
                  wrap: true,
                  cell: (r) => (
                    <MediaCell
                      src={r.client_logo_url}
                      name={r.client_name}
                      shape="square"
                      size={28}
                      title={r.client_name || '—'}
                    />
                  ),
                },
              ]),
          {
            name: t('winner', 'Winner'),
            minWidth: '180px',
            wrap: true,
            cell: winnerCell,
          },
          {
            name: t('dsm_bid_amount', 'Bid amount'),
            right: true,
            minWidth: '120px',
            cell: (r) => <Money value={r.bid_amount} />,
          },
          {
            name: t('delivery_status', 'Status'),
            minWidth: '140px',
            cell: (r) => <StatusBadge status={DELIVERY_STATUS[r.delivery_status]} />,
          },
        ];
    }
  })();

  // summary + payment split bar
  const s = summary || {};
  const payTotal = Number(s.bidder_total_amount) || 0;
  const pct = (v) => (payTotal > 0 ? ((Number(v) || 0) / payTotal) * 100 : 0);

  const tiles = [
    {
      key: 'auctions',
      icon: Gavel,
      label: t('auctions', 'Auctions'),
      value: fmtCount(s.auctions_count),
      tab: 'auctions',
    },
    !isClientRole && {
      key: 'clients',
      icon: Building2,
      label: t('ds_clients', 'Clients'),
      value: fmtCount(s.clients_count),
      tab: 'clients',
    },
    {
      key: 'bidders',
      icon: Users,
      label: t('ds_bidders', 'Bidders'),
      value: fmtCount(s.bidders_count),
      tab: 'bidders',
    },
  ].filter(Boolean);

  const payTiles = [
    {
      key: 'total',
      label: t('dsm_bidder_total', 'Bidder total'),
      value: s.bidder_total_amount,
      filter: 'all',
    },
    {
      key: 'paid',
      label: t('dsm_completed', 'Completed'),
      value: s.bidder_paid_amount,
      filter: 'paid',
    },
    {
      key: 'awaiting',
      label: awaitingLabel(),
      value: s.bidder_awaiting_amount,
      filter: 'awaiting',
    },
    {
      key: 'pending',
      label: t('dsm_pending', 'Pending'),
      value: s.bidder_pending_amount,
      filter: 'pending',
    },
  ];

  const openPayments = (filter) => {
    setTab('payments');
    setPayment(filter);
    setPage(1);
  };

  const rangeLabel = [filters?.startDate, filters?.endDate].filter(Boolean).join(' – ');

  return createPortal(
    <div
      className="dsm-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={dialogRef}
        className="dsm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dsm-title"
        tabIndex={-1}
        style={{ '--ds-tone': tone }}
      >
        {/* header */}
        <header className="dsm-head">
          <span className="dsm-icon">
            <StatusIcon size={18} strokeWidth={2.2} />
          </span>
          <div className="dsm-titles">
            <h2 id="dsm-title">{title}</h2>
            <p>
              {t('dsm_subtitle', 'Detailed listing')}
              {rangeLabel ? ` · ${rangeLabel}` : ''}
            </p>
          </div>
          <button
            type="button"
            className="dsm-close"
            onClick={onClose}
            aria-label={t('close', 'Close')}
          >
            <X size={18} />
          </button>
        </header>

        <div className="dsm-body">
          {/* summary */}
          <section className="dsm-summary" aria-busy={!summary}>
            <div className="dsm-tiles">
              {tiles.map(({ key, icon: Icon, label, value, tab: target }) => (
                <button
                  key={key}
                  type="button"
                  className={`dsm-tile ${tab === target ? 'is-active' : ''}`}
                  onClick={() => changeTab(target)}
                >
                  <Icon size={15} />
                  <span className="dsm-tile-label">{label}</span>
                  {summary ? <b>{value}</b> : <i className="dsm-skel dsm-skel--num" />}
                </button>
              ))}
            </div>

            <div className="dsm-pay">
              <div
                className="dsm-pay-bar"
                role="img"
                aria-label={t('dsm_bidder_payments', 'Bidder payments')}
              >
                <span className="is-paid" style={{ width: `${pct(s.bidder_paid_amount)}%` }} />
                <span
                  className="is-awaiting"
                  style={{ width: `${pct(s.bidder_awaiting_amount)}%` }}
                />
                <span
                  className="is-pending"
                  style={{ width: `${pct(s.bidder_pending_amount)}%` }}
                />
              </div>
              <div className="dsm-pay-tiles">
                {payTiles.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    className={`dsm-pay-tile is-${p.key} ${tab === 'payments' && payment === p.filter ? 'is-active' : ''}`}
                    onClick={() => openPayments(p.filter)}
                  >
                    <span className="dsm-pay-label">
                      {p.key !== 'total' && <i className="dsm-dot" />}
                      {p.label}
                    </span>
                    {summary ? (
                      <Money value={p.value} />
                    ) : (
                      <i className="dsm-skel dsm-skel--money" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* tabs */}
          <div className="dsm-tabs" role="tablist">
            {tabs.map(({ key, icon: Icon, label }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                className={`dsm-tab ${tab === key ? 'is-active' : ''}`}
                onClick={() => changeTab(key)}
              >
                <Icon size={15} />
                {label()}
              </button>
            ))}
          </div>

          {/* toolbar */}
          <div className="dsm-toolbar">
            <label className="dsm-search">
              <Search size={15} />
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('dsm_search', 'Search auction, group, client or bidder')}
                aria-label={t('search', 'Search')}
              />
            </label>

            {tab === 'payments' && (
              <div
                className="dsm-chips"
                role="radiogroup"
                aria-label={t('dsm_payment_filter', 'Payment filter')}
              >
                {PAYMENT_FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    role="radio"
                    aria-checked={payment === f.key}
                    className={`dsm-chip ${payment === f.key ? 'is-active' : ''}`}
                    onClick={() => {
                      setPayment(f.key);
                      setPage(1);
                    }}
                  >
                    {f.label()}
                  </button>
                ))}
              </div>
            )}

            <span className="dsm-count">
              {fmtCount(total)} {t('dsm_results', 'results')}
            </span>
          </div>

          {/* table */}
          <div className="dsm-table">
            {error ? (
              <div className="dsm-empty is-error">{error}</div>
            ) : loading && !rows.length ? (
              <TableSkeleton columns={6} rows={8} />
            ) : (
              <DataTable
                key={`${tab}-${payment}`} // resets the pager UI when the view changes
                columns={columns}
                data={rows}
                customStyles={tableStyles}
                highlightOnHover
                responsive
                progressPending={loading}
                progressComponent={<TableSkeleton columns={6} rows={8} />}
                pagination
                paginationServer
                paginationTotalRows={total}
                paginationPerPage={limit}
                paginationDefaultPage={page}
                paginationRowsPerPageOptions={[15, 25, 50, 100]}
                paginationComponentOptions={{
                  rowsPerPageText: t('rows_per_page', 'Rows per page'),
                  rangeSeparatorText: t('out', 'of'),
                }}
                onChangePage={(p) => setPage(p)}
                onChangeRowsPerPage={(l) => {
                  setLimit(l);
                  setPage(1);
                }}
                noDataComponent={
                  <div className="dsm-empty">
                    <Inbox size={22} />
                    {t('no_data_available', 'No data available')}
                  </div>
                }
              />
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DeliveryStatusDetailsModal;
